import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, Upload, Pause, Play, Square, Download, Users, FileText,
         AlertTriangle, RefreshCw, Ban, Trash2 } from "lucide-react";
import { WabaDiagnostics } from "../../components/WabaDiagnostics";
import { useAuth } from "../../context/AuthContext";
import { WabaDeliveryReport } from "../../components/WabaDeliveryReport";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const err = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || "").filter(Boolean).join("; ");
  return e?.message || "Ошибка";
};

const card = { background: "white", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16 };
const label = { fontFamily: "Manrope", fontSize: 11, fontWeight: 700, color: "#6b7280",
                letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 6 };
const input = { width: "100%", border: "1px solid #e5e7eb", borderRadius: 6, padding: "9px 11px",
                fontFamily: "Manrope", fontSize: 13, outline: "none", boxSizing: "border-box" };
const btnGold = { background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
                  border: "none", borderRadius: 6, padding: "11px 16px", cursor: "pointer",
                  fontFamily: "Manrope", fontWeight: 700, fontSize: 13,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 7 };
const btnGhost = { background: "white", color: "#0A0A0A", border: "1px solid #d1d5db",
                   borderRadius: 6, padding: "9px 13px", cursor: "pointer",
                   fontFamily: "Manrope", fontWeight: 600, fontSize: 12.5,
                   display: "flex", alignItems: "center", gap: 6 };

const STATUS_RU = { running: "Идёт", paused: "Пауза", done: "Завершена",
                     cancelled: "Остановлена", error: "Сбой",
                     waiting_limit: "Ждёт суточный лимит" };

export default function AdminBroadcast() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cfg, setCfg] = useState(null);
  const [owner, setOwner] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplError, setTplError] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [active, setActive] = useState(null);
  const [optouts, setOptouts] = useState({ items: [], count: 0 });
  const [showOptouts, setShowOptouts] = useState(false);
  const [optoutText, setOptoutText] = useState("");

  const [numbersText, setNumbersText] = useState("");
  const [audience, setAudience] = useState(null);
  const [tplKey, setTplKey] = useState("");
  const [sender, setSender] = useState("");
  const [params, setParams] = useState([]);
  // Шапка шаблона: у маркетинговых шаблонов там обычно картинка.
  const [headerType, setHeaderType] = useState("none");
  const [headerValue, setHeaderValue] = useState("");
  const [headerName, setHeaderName] = useState("");
  const headerRef = useRef(null);
  const [rate, setRate] = useState(20);
  const [dailyLimit, setDailyLimit] = useState(250);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const fileRef = useRef(null);
  const activeRequest = useRef(0);

  // Если WABA_ID не задан (список шаблонов не тянется), шаблон можно указать
  // руками — для отправки Meta нужно только имя, язык и переменные.
  const [manual, setManual] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualLang, setManualLang] = useState("ru");
  const [manualParams, setManualParams] = useState(0);
  const selected = templates.find(t => `${t.name}|${t.language}` === tplKey);
  const tpl = manual
    ? (manualName.trim()
        ? { name: manualName.trim(), language: manualLang.trim() || "ru",
            params_count: Number(manualParams) || 0, body: "" }
        : null)
    : selected;

  const loadBase = async () => {
    try {
      const c = await axios.get(`${API}/admin/waba/config`, { withCredentials: true });
      setCfg(c.data);
      const first = c.data.senders?.[0];
      if (!sender && first) { setSender(first.id); setRate(first.default_rate || 20); }
      else setRate(c.data.default_rate_per_min || 20);
    } catch (e) { setError(err(e)); }
    try {
      const o = await axios.get(`${API}/admin/waba/inbound-owner`, { withCredentials: true });
      setOwner(o.data);
    } catch { /* noop */ }
    loadCampaigns();
    loadOptouts();
  };

  // Шаблоны живут внутри WABA, а номера лежат в разных WABA — тянем список
  // именно для выбранного отправителя, иначе имя шаблона может не найтись.
  const loadTemplates = async (phoneId) => {
    try {
      const t = await axios.get(`${API}/admin/waba/templates`, {
        params: phoneId ? { phone_id: phoneId } : {}, withCredentials: true });
      setTemplates(t.data.items || []); setTplError("");
    } catch (e) { setTplError(err(e)); }
  };

  const loadCampaigns = async () => {
    try {
      const r = await axios.get(`${API}/admin/waba/campaigns`, { withCredentials: true });
      setCampaigns(r.data.items || []);
    } catch (e) { setError(err(e)); }
  };
  const loadOptouts = async () => {
    try {
      const r = await axios.get(`${API}/admin/waba/optouts`, { withCredentials: true });
      setOptouts(r.data);
    } catch (e) { setError(err(e)); }
  };

  // Initial load only; campaign updates are polled separately below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadBase(); }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTemplates(sender); setTplKey(""); }, [sender]);

  // Живые счётчики, пока рассылка идёт
  useEffect(() => {
    const t = setInterval(() => {
      loadCampaigns();
      if (active) refreshActive(active.id);
    }, 6000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [active?.id]);

  const refreshActive = async (id) => {
    const request = ++activeRequest.current;
    try {
      const r = await axios.get(`${API}/admin/waba/campaigns/${id}`, { withCredentials: true });
      if (request === activeRequest.current) setActive(r.data);
    } catch (e) { setError(err(e)); }
  };

  const [crmFrom, setCrmFrom] = useState("");
  const [crmTo, setCrmTo] = useState("");
  const [crmStatus, setCrmStatus] = useState("");
  const [crmManager, setCrmManager] = useState("");
  const [crmMeta, setCrmMeta] = useState({ statuses: [], managers: [] });

  const parseNumbers = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const r = await axios.post(`${API}/admin/waba/audience/parse`,
        { text: numbersText }, { withCredentials: true });
      setAudience(r.data);
    } catch (e) { setError(err(e)); } finally { setBusy(false); }
  };

  // Оставить только тех, кому по этой базе ещё ничего не отправляли.
  const dropAlreadyContacted = async () => {
    const skip = new Set(audience?.already_contacted || []);
    const fresh = (audience?.ready || []).filter(p => !skip.has(p));
    if (!fresh.length) { setError("После исключения не осталось ни одного номера"); return; }
    setBusy(true); setError(""); setNotice("");
    try {
      const text = fresh.join("\n");
      const r = await axios.post(`${API}/admin/waba/audience/parse`,
        { text }, { withCredentials: true });
      setNumbersText(text);
      setAudience(r.data);
      setNotice(`Убрано ${skip.size} номеров из истории рассылок. Осталось: ${r.data.total}`);
    } catch (e) { setError(err(e)); } finally { setBusy(false); }
  };

  // Номера, переданные из Mini CRM кнопкой «Рассылка WhatsApp (N)».
  useEffect(() => {
    let prefill = "";
    try {
      prefill = sessionStorage.getItem("waba_prefill_numbers") || "";
      sessionStorage.removeItem("waba_prefill_numbers");
    } catch { /* noop */ }
    if (!prefill) return;
    setNumbersText(prefill);
    (async () => {
      try {
        const r = await axios.post(`${API}/admin/waba/audience/parse`,
          { text: prefill }, { withCredentials: true });
        setAudience(r.data);
        setNotice(`Из CRM подставлено ${r.data.total} номеров — выберите шаблон и отправьте.`);
      } catch (e) { setError(err(e)); }
    })();
  }, []);

  // Справочники для блока «Собрать из CRM»
  useEffect(() => {
    (async () => {
      try {
        const [m, mg] = await Promise.all([
          axios.get(`${API}/admin/crm/meta`, { withCredentials: true }),
          axios.get(`${API}/admin/crm/managers`, { withCredentials: true }),
        ]);
        setCrmMeta({ statuses: m.data?.statuses || [], managers: mg.data || [] });
      } catch { /* noop */ }
    })();
  }, []);

  const collectFromCrm = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const r = await axios.post(`${API}/admin/waba/audience/from-crm`,
        { date_from: crmFrom, date_to: crmTo || crmFrom, status: crmStatus, manager_id: crmManager },
        { withCredentials: true });
      setAudience(r.data);
      setNumbersText((r.data.ready || []).join("\n"));
      setNotice(`Найдено заявок: ${r.data.matched}. К отправке: ${r.data.total}.`);
    } catch (e) { setError(err(e)); } finally { setBusy(false); }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await axios.post(`${API}/admin/waba/audience/upload`, fd,
        { withCredentials: true });
      setAudience(r.data);
      setNumbersText((r.data.ready || []).join("\n"));
    } catch (e2) { setError(err(e2)); } finally { setBusy(false); }
  };

  const uploadHeader = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await axios.post(`${API}/admin/waba/media/upload`, fd, { withCredentials: true });
      setHeaderValue(r.data.url); setHeaderName(r.data.filename);
    } catch (e2) { setError(err(e2)); } finally { setBusy(false); }
  };

  const launch = async () => {
    if (!audience?.ready?.length || !tpl || !sender) return;
    if (!window.confirm(`Отправить шаблон «${tpl.name}» на ${audience.ready.length} номеров?`)) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const r = await axios.post(`${API}/admin/waba/campaigns`, {
        name, template_name: tpl.name, language: tpl.language, sender_id: sender,
        params: params.slice(0, tpl.params_count), numbers: audience.ready,
        rate_per_min: Number(rate) || 20,
        daily_limit: Number(dailyLimit) || 250,
        header_type: headerType, header_value: headerValue,
      }, { withCredentials: true });
      setNotice(`Рассылка запущена: в очереди ${r.data.queued} номеров`);
      setAudience(null); setNumbersText(""); setName("");
      await loadCampaigns();
      await refreshActive(r.data.id);
    } catch (e) { setError(err(e)); } finally { setBusy(false); }
  };

  const control = async (id, action) => {
    if (action === "resume" && !window.confirm("Продолжить только оставшуюся очередь? Номера с ошибками повторно отправляться не будут. Если Meta ограничила качество, сначала проверьте номер и шаблон.")) return;
    try {
      await axios.post(`${API}/admin/waba/campaigns/${id}/${action}`, {},
        { withCredentials: true });
      if (action === "resume") setNotice("Рассылка продолжена: очередь снова в работе. Получатели с ошибками на отправку не возвращены — для них есть «Подготовить повтор».");
      await loadCampaigns(); await refreshActive(id);
    } catch (e) { setError(err(e)); }
  };

  const changeRate = async (id, value) => {
    try {
      await axios.post(`${API}/admin/waba/campaigns/${id}/rate`,
        { rate_per_min: Number(value) }, { withCredentials: true });
      setNotice(`Темп рассылки изменён: ${value} сообщений в минуту`);
      await refreshActive(id); await loadCampaigns();
    } catch (e) { setError(err(e)); }
  };

  const changeDailyLimit = async (id, value) => {
    const limit = Number(value);
    if (!limit || limit < 1) { setError("Суточный лимит должен быть числом больше нуля"); return; }
    if (limit === Number(active?.daily_limit)) return;
    try {
      await axios.post(`${API}/admin/waba/campaigns/${id}/daily-limit`,
        { daily_limit: limit }, { withCredentials: true });
      setNotice(`Суточный лимит изменён: ${limit} сообщений в сутки. Если лимит на сегодня ещё не исчерпан, отправка продолжится сразу.`);
      await refreshActive(id); await loadCampaigns();
    } catch (e) { setError(err(e)); }
  };

  const saveOwner = async (userId) => {
    try {
      const r = await axios.post(`${API}/admin/waba/inbound-owner`, { user_id: userId },
        { withCredentials: true });
      setOwner(o => ({ ...o, owner_id: r.data.owner_id, owner_name: r.data.owner_name || "" }));
      setNotice(userId
        ? `Входящие со второго номера закреплены за: ${r.data.owner_name}`
        : "Входящие со второго номера больше не попадают в CRM");
    } catch (e) { setError(err(e)); }
  };

  const selectSender = (id) => {
    setSender(id);
    const s = (cfg?.senders || []).find(x => x.id === id);
    if (s?.default_rate) setRate(s.default_rate);
  };

  const addOptouts = async () => {
    try {
      const r = await axios.post(`${API}/admin/waba/optouts`, { text: optoutText },
        { withCredentials: true });
      setOptoutText(""); setNotice(`В чёрный список добавлено: ${r.data.added}`);
      loadOptouts();
    } catch (e) { setError(err(e)); }
  };

  return (
    <div data-testid="admin-broadcast" style={{ paddingBottom: 40 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: 30, fontWeight: 400, margin: 0 }}>
          Рассылки WhatsApp (Meta)
        </h2>
        {isAdmin && (
          <a data-testid="waba-export-base-link" href={`${API}/admin/waba/export/delivered-base`}
            title="Все номера, которым сообщения были доставлены — без дублей"
            style={{ ...btnGhost, textDecoration: "none", color: "#15803d", borderColor: "#bbf7d0" }}>
            <Download size={13} /> База доставленных
          </a>
        )}
      </div>
      <div style={{ fontFamily: "Manrope", fontSize: 12.5, color: "#6b7280",
                     marginTop: 6, marginBottom: 18, maxWidth: 760, lineHeight: 1.5 }}>
        Официальная рассылка по одобренным шаблонам Meta: загрузите базу номеров,
        выберите шаблон и запустите. Номера нормализуются автоматически, дубли и
        отписавшиеся исключаются.
      </div>

      {cfg && !cfg.configured && (
        <div data-testid="waba-not-configured" style={{ ...card, background: "#FFFBEB",
              borderColor: "#FDE68A", marginBottom: 18, fontFamily: "Manrope", fontSize: 12.5,
              color: "#78350F", lineHeight: 1.55 }}>
          <b><AlertTriangle size={14} style={{ verticalAlign: "-2px" }} /> Нужно подключить Meta.</b>
          <div style={{ marginTop: 6 }}>
            Пришлите два значения, и рассылка заработает: <b>постоянный Access Token</b>
            {" "}системного пользователя (права whatsapp_business_messaging и
            whatsapp_business_management) и <b>WABA ID</b> из WhatsApp Manager → Обзор.
            Номера-отправители уже прописаны.
          </div>
          {cfg.webhook_url && (
            <div style={{ marginTop: 8 }}>
              Вебхук для статусов: <code>{cfg.webhook_url}</code> · verify token:{" "}
              <code>{cfg.webhook_verify_token}</code>
            </div>
          )}
        </div>
      )}

      {error && (
        <div data-testid="waba-error" style={{ background: "#fef2f2", border: "1px solid #fecaca",
              color: "#b91c1c", borderRadius: 8, padding: "10px 12px", fontFamily: "Manrope",
              fontSize: 12.5, marginBottom: 14 }}>{error}</div>
      )}
      {notice && (
        <div data-testid="waba-notice" style={{ background: "#ecfdf5", border: "1px solid #a7f3d0",
              color: "#047857", borderRadius: 8, padding: "10px 12px", fontFamily: "Manrope",
              fontSize: 12.5, marginBottom: 14 }}>{notice}</div>
      )}

      <WabaDiagnostics />
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 330px), 1fr))" }}>
        {/* Шаг 1 — база номеров */}
        <div style={card}>
          <div style={label}><Users size={12} style={{ verticalAlign: "-2px" }} /> 1. База номеров</div>
          <textarea data-testid="waba-numbers-input" value={numbersText}
            onChange={e => setNumbersText(e.target.value)}
            placeholder={"Вставьте номера — по одному в строке или через запятую\n77019407652\n+7 747 687 0484"}
            style={{ ...input, minHeight: 120, resize: "vertical", fontFamily: "monospace", fontSize: 12.5 }} />
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <button data-testid="waba-parse-btn" onClick={parseNumbers} disabled={busy || !numbersText.trim()}
              style={{ ...btnGhost, opacity: numbersText.trim() ? 1 : 0.5 }}>
              <RefreshCw size={13} /> Проверить список
            </button>
            <input data-testid="waba-audience-file-input" ref={fileRef} type="file" hidden accept=".csv,.txt,.xlsx" onChange={uploadFile} />
            <button data-testid="waba-upload-btn" onClick={() => fileRef.current?.click()}
              disabled={busy} style={btnGhost}>
              <Upload size={13} /> CSV / Excel
            </button>
            <button data-testid="waba-optouts-btn" onClick={() => setShowOptouts(v => !v)} style={btnGhost}>
              <Ban size={13} /> Чёрный список ({optouts.count})
            </button>
          </div>

          <div data-testid="waba-crm-collect" style={{ marginTop: 12, padding: "10px 12px",
                background: "#fafaf9", border: "1px dashed #e5e7eb", borderRadius: 8 }}>
            <div style={{ fontFamily: "Manrope", fontSize: 11.5, fontWeight: 700, color: "#6b7280",
                           letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>
              Собрать из CRM по дате обращения
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <input data-testid="waba-crm-from" type="date" value={crmFrom}
                     onChange={e => { setCrmFrom(e.target.value); if (!crmTo) setCrmTo(e.target.value); }}
                     style={{ ...input, width: 150, marginTop: 0 }} />
              <span style={{ fontFamily: "Manrope", fontSize: 12, color: "#9ca3af" }}>—</span>
              <input data-testid="waba-crm-to" type="date" value={crmTo}
                     onChange={e => setCrmTo(e.target.value)}
                     style={{ ...input, width: 150, marginTop: 0 }} />
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
              {[{ id: "today", label: "Сегодня", days: 0 },
                { id: "yst", label: "Вчера", days: 1 },
                { id: "w", label: "Неделю назад", days: 7 }].map(p => (
                <button key={p.id} data-testid={`waba-crm-quick-${p.id}`} type="button"
                  onClick={() => {
                    const d = new Date(Date.now() - p.days * 86400_000);
                    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                    setCrmFrom(iso); setCrmTo(iso);
                  }}
                  style={{ ...btnGhost, padding: "5px 9px", fontSize: 11 }}>{p.label}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
              <select data-testid="waba-crm-status" value={crmStatus} onChange={e => setCrmStatus(e.target.value)}
                      style={{ ...input, width: 170, marginTop: 0 }}>
                <option value="">Все статусы</option>
                {crmMeta.statuses.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <select data-testid="waba-crm-manager" value={crmManager} onChange={e => setCrmManager(e.target.value)}
                      style={{ ...input, width: 170, marginTop: 0 }}>
                <option value="">Все менеджеры</option>
                <option value="free">— Свободные —</option>
                {crmMeta.managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <button data-testid="waba-crm-collect-btn" onClick={collectFromCrm}
              disabled={busy || !crmFrom}
              style={{ ...btnGhost, marginTop: 8, opacity: crmFrom ? 1 : 0.5 }}>
              <Users size={13} /> Подставить номера
            </button>
            <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
              Отписавшиеся, недоставляемые номера и те, кто в 24-часовом ограничении Meta,
              исключаются автоматически.
            </div>
          </div>

          {audience && (
            <div data-testid="waba-audience-summary" style={{ marginTop: 12, fontFamily: "Manrope",
                  fontSize: 12.5, color: "#374151", lineHeight: 1.7 }}>
              <div>К отправке: <b data-testid="waba-ready-count">{audience.total}</b></div>
              {audience.duplicates_removed > 0 && <div>Дубли удалены: {audience.duplicates_removed}</div>}
              {audience.invalid?.length > 0 && (
                <div style={{ color: "#b45309" }}>Некорректные ({audience.invalid.length}): {audience.invalid.slice(0, 5).join(", ")}…</div>
              )}
              {audience.optout_excluded?.length > 0 && (
                <div style={{ color: "#b45309" }}>Исключены по отписке: {audience.optout_excluded.length}</div>
              )}
              {audience.undeliverable_excluded?.length > 0 && <div data-testid="waba-audience-undeliverable" style={{ color: "#b45309" }}>Исключены как недоставляемые (131026): {audience.undeliverable_excluded.length}</div>}
              {audience.cooldown_excluded?.length > 0 && <div data-testid="waba-audience-cooldown" style={{ color: "#b45309" }}>Исключены до истечения 24 часов (131049): {audience.cooldown_excluded.length}</div>}
              {audience.already_contacted?.length > 0 && (
                <div data-testid="waba-audience-previous" style={{ color: "#6b7280",
                      display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span>Уже есть в истории рассылок (не подтверждение доставки): {audience.already_contacted.length}</span>
                  <button data-testid="waba-drop-already-contacted" onClick={dropAlreadyContacted}
                    disabled={busy}
                    title="Оставить только номера, которым ещё ни разу не отправляли"
                    style={{ ...btnGhost, padding: "4px 10px", fontSize: 11.5 }}>
                    Убрать ({audience.already_contacted.length})
                  </button>
                </div>
              )}
            </div>
          )}

          {showOptouts && (
            <div style={{ marginTop: 12, borderTop: "1px solid #f1f5f9", paddingTop: 10 }}>
              <div style={{ ...label, marginBottom: 4 }}>Чёрный список</div>
              <div style={{ display: "flex", gap: 6 }}>
                <input data-testid="waba-optout-input" value={optoutText}
                  onChange={e => setOptoutText(e.target.value)}
                  placeholder="Номера через запятую" style={input} />
                <button data-testid="waba-optout-add" onClick={addOptouts} style={btnGhost}>+</button>
              </div>
              <div style={{ maxHeight: 130, overflowY: "auto", marginTop: 8 }}>
                {optouts.items.map(o => (
                  <div key={o.phone} style={{ display: "flex", alignItems: "center", gap: 6,
                        fontFamily: "Manrope", fontSize: 12, padding: "3px 0", color: "#374151" }}>
                    <span style={{ flex: 1 }}>+{o.phone} <span style={{ color: "#9ca3af" }}>{o.reason}</span></span>
                    <button data-testid={`waba-optout-delete-${o.phone}`} aria-label={`Убрать ${o.phone} из чёрного списка`} onClick={async () => {
                      try {
                        await axios.delete(`${API}/admin/waba/optouts/${o.phone}`, { withCredentials: true });
                        loadOptouts();
                      } catch (e) { setError(err(e)); }
                      }} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626" }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Шаг 2 — шаблон */}
        <div style={card}>
          <div style={label}><FileText size={12} style={{ verticalAlign: "-2px" }} /> 2. Шаблон Meta</div>
          {tplError && (
            <div data-testid="waba-template-error" style={{ fontFamily: "Manrope", fontSize: 12, color: "#b45309", marginBottom: 8 }}>
              Не удалось получить шаблоны: {tplError}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <button data-testid="waba-mode-list" onClick={() => setManual(false)}
              style={{ ...btnGhost, flex: 1, justifyContent: "center",
                        background: manual ? "white" : "#0A0A0A",
                        color: manual ? "#0A0A0A" : "#D4B878",
                        borderColor: manual ? "#d1d5db" : "#C5A059" }}>
              Из списка Meta
            </button>
            <button data-testid="waba-mode-manual" onClick={() => setManual(true)}
              style={{ ...btnGhost, flex: 1, justifyContent: "center",
                        background: manual ? "#0A0A0A" : "white",
                        color: manual ? "#D4B878" : "#0A0A0A",
                        borderColor: manual ? "#C5A059" : "#d1d5db" }}>
              Указать вручную
            </button>
          </div>

          {manual && (
            <div data-testid="waba-manual-template">
              <div style={{ ...label, marginBottom: 4 }}>Имя шаблона (как в WhatsApp Manager)</div>
              <input data-testid="waba-manual-name" value={manualName} list="waba-known-templates"
                onChange={e => setManualName(e.target.value)}
                placeholder="например our_clients" style={input} />
              <datalist id="waba-known-templates">
                <option value="our_clients" />
                <option value="puhoviki" />
                <option value="palto_normeet" />
                <option value="angiolini_it" />
                <option value="clients_angiolini" />
              </datalist>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ ...label, marginBottom: 4 }}>Язык</div>
                  <input data-testid="waba-manual-lang" value={manualLang}
                    onChange={e => setManualLang(e.target.value)} placeholder="ru" style={input} />
                </div>
                <div style={{ width: 130 }}>
                  <div style={{ ...label, marginBottom: 4 }}>Переменных</div>
                  <input data-testid="waba-manual-params" type="number" min="0" max="10"
                    value={manualParams} onChange={e => setManualParams(e.target.value)} style={input} />
                </div>
              </div>
            </div>
          )}

          {!manual && (
          <select data-testid="waba-template-select" value={tplKey}
            onChange={e => {
              setTplKey(e.target.value); setParams([]);
              const t = templates.find(x => `${x.name}|${x.language}` === e.target.value);
              const f = (t?.header_format || "NONE").toLowerCase();
              setHeaderType(["image", "video", "document", "text"].includes(f) ? f : "none");
              // Подставляем картинку, которой уже пользовались с этим шаблоном.
              setHeaderValue(t?.last_header_value || "");
              setHeaderName(t?.last_header_value ? "картинка из прошлой рассылки" : "");
            }} style={input}>
            <option value="">Выберите одобренный шаблон…</option>
            {templates.map(t => (
              <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
                {t.name} · {t.language}
              </option>
            ))}
          </select>
          )}

          {tpl && (
            <>
              {tpl.body && (
                <div style={{ background: "#F9FAFB", border: "1px solid #f1f5f9", borderRadius: 8,
                               padding: "10px 12px", marginTop: 10, fontFamily: "Manrope",
                               fontSize: 12.5, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                  {tpl.body}
                </div>
              )}
              {Array.from({ length: tpl.params_count }).map((_, i) => (
                <div key={i} style={{ marginTop: 8 }}>
                  <div style={{ ...label, marginBottom: 4 }}>{`Переменная {{${i + 1}}}`}</div>
                  <input data-testid={`waba-param-${i + 1}`} value={params[i] || ""}
                    onChange={e => setParams(p => { const n = [...p]; n[i] = e.target.value; return n; })}
                    placeholder="Значение для всех получателей" style={input} />
                </div>
              ))}
            </>
          )}

          {tpl && (
            <div data-testid="waba-header-block" style={{ marginTop: 12,
                  borderTop: "1px solid #f1f5f9", paddingTop: 10 }}>
              <div style={{ ...label, marginBottom: 4 }}>Шапка шаблона</div>
              <select data-testid="waba-header-type" value={headerType}
                onChange={e => { setHeaderType(e.target.value); setHeaderValue(""); setHeaderName(""); }}
                style={input}>
                <option value="none">Без шапки</option>
                <option value="image">Картинка</option>
                <option value="video">Видео</option>
                <option value="document">Документ</option>
                <option value="text">Текст</option>
              </select>
              {headerType === "text" && (
                <input data-testid="waba-header-text" value={headerValue}
                  onChange={e => setHeaderValue(e.target.value)}
                  placeholder="Текст шапки" style={{ ...input, marginTop: 8 }} />
              )}
              {["image", "video", "document"].includes(headerType) && (
                <>
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <input data-testid="waba-header-file-input" ref={headerRef} type="file" hidden onChange={uploadHeader}
                      accept={headerType === "image" ? "image/*" : headerType === "video" ? "video/*" : "*"} />
                    <button data-testid="waba-header-upload" onClick={() => headerRef.current?.click()}
                      disabled={busy} style={btnGhost}>
                      <Upload size={13} /> Загрузить файл
                    </button>
                    {headerName && (
                      <span style={{ fontFamily: "Manrope", fontSize: 12, color: "#047857",
                                      alignSelf: "center" }}>✓ {headerName}</span>
                    )}
                  </div>
                  <input data-testid="waba-header-url" value={headerValue}
                    onChange={e => { setHeaderValue(e.target.value); setHeaderName(""); }}
                    placeholder="…или вставьте прямую ссылку на файл"
                    style={{ ...input, marginTop: 8, fontSize: 11.5 }} />
                  {headerType === "image" && headerValue && (
                    <img src={headerValue} alt="" style={{ marginTop: 8, width: "100%",
                          maxHeight: 140, objectFit: "cover", borderRadius: 6 }} />
                  )}
                </>
              )}
              <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#8a8a89",
                             marginTop: 6, lineHeight: 1.45 }}>
                Если шаблон создан с картинкой в шапке — её нужно приложить, иначе Meta
                вернёт ошибку «Parameter format does not match».
              </div>
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <div style={{ ...label, marginBottom: 4 }}>Отправлять с номера</div>
            <select data-testid="waba-sender-select" value={sender}
              onChange={e => selectSender(e.target.value)} style={input}>
              {(cfg?.senders || []).map(s => (
                <option key={s.id} value={s.id}>
                  {s.label}{s.in_crm ? "" : " · только рассылки"}
                </option>
              ))}
            </select>
            {sender && !(cfg?.senders || []).find(s => s.id === sender)?.in_crm && (
              <div data-testid="waba-secondary-note"
                style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8,
                          padding: "8px 10px", marginTop: 8, fontFamily: "Manrope",
                          fontSize: 11.5, color: "#92400e", lineHeight: 1.45 }}>
                Это номер только для рассылок — переписки в Mini CRM менеджерам не попадают.
                Ответы клиентов закрепляются за ответственным
                {owner?.owner_name ? `: ${owner.owner_name}` : " (не выбран — ответы не сохраняются)"}.
                Темп отправки сам по себе не гарантирует доставку и не снимает ограничения Meta.
              </div>
            )}
            {owner?.staff?.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ ...label, marginBottom: 4 }}>
                  Ответственный за ответы с номера «только рассылки»
                </div>
                <select data-testid="waba-inbound-owner-select"
                  value={owner.owner_id || ""} onChange={e => saveOwner(e.target.value)}
                  style={input}>
                  <option value="">Не сохранять в CRM</option>
                  {owner.staff.map(s => (
                    <option key={s.id} value={s.id}>{s.name} · {s.role}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Шаг 3 — запуск */}
        <div style={card}>
          <div style={label}><Send size={12} style={{ verticalAlign: "-2px" }} /> 3. Запуск</div>
          <div style={{ ...label, marginBottom: 4 }}>Название рассылки (необязательно)</div>
          <input data-testid="waba-name-input" value={name} onChange={e => setName(e.target.value)}
            placeholder="Например: Пуховики · сентябрь" style={input} />
          <div style={{ ...label, marginBottom: 4, marginTop: 10 }}>Темп, сообщений в минуту</div>
          <input data-testid="waba-rate-input" type="number" min="1" max="120" value={rate}
            onChange={e => setRate(e.target.value)} style={{ ...input, width: 110 }} />
          <div style={{ ...label, marginBottom: 4, marginTop: 10 }}>Локальный лимит в сутки (UTC)</div>
          <input data-testid="waba-daily-limit" type="number" min="1" max="100000"
            value={dailyLimit} onChange={e => setDailyLimit(e.target.value)}
            style={{ ...input, width: 110 }} />
          <div data-testid="waba-rate-policy" style={{ fontFamily: "Manrope", fontSize: 11, color: "#6b7280", marginTop: 6, lineHeight: 1.45 }}>
            Это ограничитель приложения, не актуальный лимит Meta. При 131048 рассылка
            приостанавливается: проблемное сообщение сохраняется как ошибка и не
            возвращается в очередь. Снижение темпа не отменяет ограничения качества.
          </div>
          <button data-testid="waba-launch-btn" onClick={launch}
            disabled={busy || !audience?.ready?.length || !tpl || !sender}
            style={{ ...btnGold, marginTop: 14, width: "100%",
                      opacity: (!busy && audience?.ready?.length && tpl && sender) ? 1 : 0.5 }}>
            <Send size={14} /> Запустить рассылку
            {audience?.ready?.length ? ` (${audience.ready.length})` : ""}
          </button>
        </div>
      </div>

      {/* Активная рассылка */}
      {active && (
        <div data-testid="waba-active-campaign" style={{ ...card, marginTop: 18 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                         gap: 10, flexWrap: "wrap" }}>
            <div>
              <div data-testid="waba-active-name" style={{ fontFamily: "Manrope", fontSize: 14, fontWeight: 700 }}>{active.name}</div>
              <div data-testid="waba-active-status" style={{ fontFamily: "Manrope", fontSize: 12, color: "#6b7280" }}>
                {active.template_name} · {active.language} · {active.sender_label} ·{" "}
                {STATUS_RU[active.status] || active.status}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontFamily: "Manrope", fontSize: 11, color: "#8a8a89",
                                textTransform: "uppercase", letterSpacing: "0.06em" }}>Темп</span>
                <select data-testid="waba-active-rate-select"
                  value={String(active.rate_per_min || 20)}
                  onChange={e => changeRate(active.id, e.target.value)}
                  style={{ ...input, width: 128, padding: "7px 8px", fontSize: 12 }}>
                  {Array.from(new Set([...[3, 6, 10, 15, 20, 30, 40, 60],
                                       Number(active.rate_per_min) || 20]))
                    .sort((a, b) => a - b).map(v => (
                    <option key={v} value={v}>{v} / мин</option>
                  ))}
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontFamily: "Manrope", fontSize: 11, color: "#8a8a89",
                                textTransform: "uppercase", letterSpacing: "0.06em" }}>Лимит в сутки</span>
                <input data-testid="waba-active-daily-limit" type="number" min={1} max={100000}
                  defaultValue={active.daily_limit || 250} key={`dl-${active.id}-${active.daily_limit}`}
                  onBlur={e => changeDailyLimit(active.id, e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                  style={{ ...input, width: 96, padding: "7px 8px", fontSize: 12 }} />
              </div>
              {["running", "waiting_limit"].includes(active.status) && (
                <button data-testid="waba-pause-btn" onClick={() => control(active.id, "pause")} style={btnGhost}>
                  <Pause size={13} /> Пауза
                </button>
              )}
              {["paused", "error", "waiting_limit"].includes(active.status) && active.queued > 0 && (
                <button data-testid="waba-resume-btn" onClick={() => control(active.id, "resume")} style={btnGhost}>
                  <Play size={13} /> {active.retry_prepared ? "Запустить повтор" : "Продолжить рассылку"}
                </button>
              )}
              {["running", "paused", "waiting_limit"].includes(active.status) && (
                <button data-testid="waba-cancel-btn" onClick={() => control(active.id, "cancel")}
                  style={{ ...btnGhost, color: "#b91c1c", borderColor: "#fca5a5" }}>
                  <Square size={13} /> Остановить
                </button>
              )}
              <a data-testid="waba-export-link" href={`${API}/admin/waba/campaigns/${active.id}/export`}
                style={{ ...btnGhost, textDecoration: "none" }}>
                <Download size={13} /> Excel
              </a>
              {isAdmin && (
                <a data-testid="waba-export-delivered-link"
                  href={`${API}/admin/waba/campaigns/${active.id}/export/delivered`}
                  title="Только номера, которым сообщение доставлено"
                  style={{ ...btnGhost, textDecoration: "none", color: "#15803d", borderColor: "#bbf7d0" }}>
                  <Download size={13} /> Доставленные
                </a>
              )}
            </div>
          </div>

          {active.pause_reason && (
            <div data-testid="waba-pause-reason"
              style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8,
                        padding: "10px 12px", marginTop: 12, fontFamily: "Manrope",
                        fontSize: 12, color: "#991b1b", lineHeight: 1.5 }}>
              {active.pause_reason}
            </div>
          )}

          <WabaDeliveryReport key={active.id} campaign={active} onRetried={(id, prepared) => {
            setNotice(prepared
              ? "Повтор по лимитам подготовлен на паузе. Сообщения не отправлены. Проверьте список и нажмите «Запустить повтор», когда будете готовы."
              : "Создана отдельная рассылка для ручного повтора. Исходная ошибка сохранена.");
            loadCampaigns(); refreshActive(id);
          }} />
        </div>
      )}

      {/* История */}
      <div style={{ ...card, marginTop: 18 }}>
        <div style={label}>История рассылок</div>
        {campaigns.length === 0 && (
          <div style={{ fontFamily: "Manrope", fontSize: 12.5, color: "#9ca3af" }}>
            Рассылок ещё не было
          </div>
        )}
        {campaigns.map(c => (
          <button type="button" key={c.id} data-testid={`waba-campaign-${c.id}`}
            onClick={() => refreshActive(c.id)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", width: "100%", textAlign: "left", background: "transparent", border: 0,
                      borderBottom: "1px solid #f8fafc", cursor: "pointer", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontFamily: "Manrope", fontSize: 13, fontWeight: 600 }}>{c.name}</div>
              <div style={{ fontFamily: "Manrope", fontSize: 11.5, color: "#9ca3af" }}>
                {new Date(c.created_at).toLocaleString("ru-RU")} · {c.created_by_name} · {c.sender_label}
              </div>
            </div>
            <div data-testid={`waba-campaign-counts-${c.id}`} style={{ fontFamily: "Manrope", fontSize: 12, color: "#374151" }}>
              {c.accepted}/{c.total} принято Meta · {c.sent} отправлено (webhook) · {c.delivered} доставлено · {c.read} прочитано
              {c.failed ? ` · ${c.failed} ошибок` : ""}
            </div>
            <div style={{ fontFamily: "Manrope", fontSize: 11, fontWeight: 700,
                           color: c.status === "running" ? "#047857" : "#6b7280" }}>
              {STATUS_RU[c.status] || c.status}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
